import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, Edit } from 'lucide-react';
import { formatNumber } from '../../utils/constants';
import { API_URL } from './helpers';

export const LiabilitiesTab = ({ liabilities, liabilityCategories, isAdmin, canDeleteLiability, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newLiability, setNewLiability] = useState({ category: '', name: '', description: '', amount: '', date: new Date().toISOString().split('T')[0], due_date: '' });
  const [editingLiability, setEditingLiability] = useState(null);

  const liabilitiesByCategory = liabilities.reduce((acc, liability) => {
    if (!acc[liability.category]) acc[liability.category] = [];
    acc[liability.category].push(liability);
    return acc;
  }, {});

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/liabilities`, {
        ...newLiability,
        amount: parseFloat(newLiability.amount),
        date: new Date(newLiability.date).toISOString(),
        due_date: newLiability.due_date ? new Date(newLiability.due_date).toISOString() : null
      });
      toast.success('Liability created successfully');
      setDialogOpen(false);
      setNewLiability({ category: '', name: '', description: '', amount: '', date: new Date().toISOString().split('T')[0], due_date: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to create liability');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingLiability) return;
    try {
      await axios.put(`${API_URL}/liabilities/${editingLiability.id}`, {
        category: editingLiability.category,
        name: editingLiability.name,
        description: editingLiability.description,
        amount: parseFloat(editingLiability.amount),
        date: new Date(editingLiability.date).toISOString(),
        due_date: editingLiability.due_date ? new Date(editingLiability.due_date).toISOString() : null
      });
      toast.success('Liability updated successfully');
      setEditDialogOpen(false);
      setEditingLiability(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update liability');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this liability?')) return;
    try {
      await axios.delete(`${API_URL}/liabilities/${id}`);
      toast.success('Liability deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete liability');
    }
  };

  const openEdit = (liability) => {
    setEditingLiability({
      ...liability,
      date: liability.date ? liability.date.split('T')[0] : new Date().toISOString().split('T')[0],
      due_date: liability.due_date ? liability.due_date.split('T')[0] : ''
    });
    setEditDialogOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Other Liabilities (Manual Entries)</h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-liability-btn"><Plus className="w-4 h-4 mr-2" />Add Liability</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add New Liability</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category *</label>
                <Select value={newLiability.category} onValueChange={(v) => setNewLiability({...newLiability, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {liabilityCategories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <Input value={newLiability.name} onChange={(e) => setNewLiability({...newLiability, name: e.target.value})} placeholder="e.g., Supplier Credit, Bank Loan" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea value={newLiability.description} onChange={(e) => setNewLiability({...newLiability, description: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Amount (RWF) *</label>
                <Input type="number" step="0.01" value={newLiability.amount} onChange={(e) => setNewLiability({...newLiability, amount: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date *</label>
                <Input type="date" value={newLiability.date} onChange={(e) => setNewLiability({...newLiability, date: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Due Date</label>
                <Input type="date" value={newLiability.due_date} onChange={(e) => setNewLiability({...newLiability, due_date: e.target.value})} />
              </div>
              <Button type="submit" className="w-full">Create Liability</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Category Summary Cards */}
      {Object.keys(liabilitiesByCategory).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          {liabilityCategories.filter(cat => liabilitiesByCategory[cat]).map(category => {
            const categoryTotal = liabilitiesByCategory[category]?.reduce((sum, l) => sum + l.amount, 0) || 0;
            return (
              <div key={category} className="p-4 bg-red-50 rounded-lg border border-red-100">
                <span className="text-sm font-medium text-red-800">{category}</span>
                <p className="text-lg font-bold text-red-700">RWF {formatNumber(categoryTotal)}</p>
                <p className="text-xs text-red-600">{liabilitiesByCategory[category]?.length || 0} items</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Liabilities Table */}
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="liabilities-table">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Category</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Description</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Amount</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Due Date</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {liabilities.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No manual liabilities recorded yet.</td></tr>
            )}
            {liabilities.map((liability) => (
              <tr key={liability.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 text-sm">{liability.category}</td>
                <td className="px-4 py-3 text-sm font-medium text-foreground">{liability.name}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{liability.description || '-'}</td>
                <td className="px-4 py-3 text-sm font-semibold text-red-600">RWF {formatNumber(liability.amount)}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {liability.due_date ? new Date(liability.due_date).toLocaleDateString() : '-'}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(liability)}><Edit className="w-4 h-4" /></Button>
                        {canDeleteLiability && <Button variant="ghost" size="sm" onClick={() => handleDelete(liability.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Liability Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Liability</DialogTitle></DialogHeader>
          {editingLiability && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category *</label>
                <Select value={editingLiability.category} onValueChange={(v) => setEditingLiability({...editingLiability, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {liabilityCategories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <Input value={editingLiability.name} onChange={(e) => setEditingLiability({...editingLiability, name: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea value={editingLiability.description || ''} onChange={(e) => setEditingLiability({...editingLiability, description: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Amount (RWF) *</label>
                <Input type="number" step="0.01" value={editingLiability.amount} onChange={(e) => setEditingLiability({...editingLiability, amount: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Due Date</label>
                <Input type="date" value={editingLiability.due_date} onChange={(e) => setEditingLiability({...editingLiability, due_date: e.target.value})} />
              </div>
              <Button type="submit" className="w-full">Update Liability</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
